import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Statistic, Table, Tag, Button, Modal, Form, Input,
    InputNumber, Space, message, Tooltip, Popconfirm, Select, Badge, Alert, Checkbox
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
    StopOutlined, CalendarOutlined, TeamOutlined, BankOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { superAdminService } from '../../services';

const { Option } = Select;

const SuperAdminDashboard = () => {
    const { t } = useTranslation(['superadmin', 'common']);
    const [loading, setLoading] = useState(true);
    const [clinics, setClinics] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [extendModalVisible, setExtendModalVisible] = useState(false);
    const [limitModalVisible, setLimitModalVisible] = useState(false);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [form] = Form.useForm();
    const [extendForm] = Form.useForm();
    const [limitForm] = Form.useForm();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const [clinicsRes, statsRes] = await Promise.all([
                superAdminService.getAllClinics({
                    page, limit: pageSize,
                    search: searchText,
                    status: statusFilter
                }),
                superAdminService.getStatistics()
            ]);

            if (clinicsRes.success) {
                setClinics(clinicsRes.data.clinics);
                setPagination({
                    current: clinicsRes.data.pagination.page,
                    pageSize: clinicsRes.data.pagination.limit,
                    total: clinicsRes.data.pagination.total
                });
            }

            if (statsRes.success) {
                setStatistics(statsRes.data);
            }
        } catch (error) {
            message.error(t('superadmin:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateClinic = async (values) => {
        setSubmitLoading(true);
        try {
            const response = await superAdminService.createClinic({
                ...values,
                subscriptionPeriod: parseInt(values.subscriptionPeriod || 365)
            });
            if (response.success) {
                message.success(t('superadmin:messages.createSuccess'));
                setCreateModalVisible(false);
                form.resetFields();
                fetchData();
            }
        } catch (error) {
            message.error(error.response?.data?.message || t('superadmin:messages.createFailed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleStatusChange = async (clinicId, newStatus) => {
        try {
            await superAdminService.updateClinicStatus(clinicId, newStatus);
            message.success(t('superadmin:messages.statusChanged', { status: newStatus }));
            fetchData();
        } catch (error) {
            message.error(t('superadmin:messages.statusChangeFailed'));
        }
    };

    const handleExtendSubscription = async (values) => {
        setSubmitLoading(true);
        try {
            await superAdminService.extendSubscription(selectedClinic.id, values.days, values.amount);
            message.success(t('superadmin:messages.extendSuccess'));
            setExtendModalVisible(false);
            extendForm.resetFields();
            fetchData();
        } catch (error) {
            message.error(t('superadmin:messages.extendFailed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateDentistLimit = async (values) => {
        setSubmitLoading(true);
        try {
            await superAdminService.updateDentistLimit(selectedClinic.id, values.dentistLimit);
            message.success(t('superadmin:messages.dentistLimitSuccess'));
            setLimitModalVisible(false);
            limitForm.resetFields();
            fetchData();
        } catch (error) {
            message.error(t('superadmin:messages.dentistLimitFailed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeleteClinic = async (clinicId) => {
        try {
            await superAdminService.deleteClinic(clinicId);
            message.success(t('superadmin:messages.deleteSuccess'));
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.message || t('superadmin:messages.deleteFailed'));
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            active: 'success',
            trial: 'processing',
            expired: 'error',
            suspended: 'warning',
            inactive: 'default'
        };
        return colors[status] || 'default';
    };

    const getDaysColor = (days) => {
        if (days <= 0) return '#ff4d4f';
        if (days <= 7) return '#faad14';
        if (days <= 30) return '#1890ff';
        return '#52c41a';
    };

    const columns = [
        {
            title: t('superadmin:columns.clinicName'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{text}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.email}</div>
                </div>
            )
        },
        {
            title: t('superadmin:columns.status'),
            dataIndex: 'subscriptionStatus',
            key: 'status',
            render: (status) => (
                <Badge status={getStatusColor(status)} text={
                    <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
                } />
            )
        },
        {
            title: t('superadmin:columns.daysRemaining'),
            dataIndex: 'remainingDays',
            key: 'remainingDays',
            render: (days) => (
                <span style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: getDaysColor(days)
                }}>
                    {days} {t('superadmin:table.days')}
                </span>
            )
        },
        {
            title: t('superadmin:columns.dentists'),
            key: 'dentists',
            render: (_, record) => (
                <div>
                    <TeamOutlined /> {record.currentDentistCount || 0} / {record.dentistLimit}
                    {record.currentDentistCount >= record.dentistLimit && (
                        <Tag color="error" style={{ marginLeft: 8 }}>{t('superadmin:table.full')}</Tag>
                    )}
                </div>
            )
        },
        {
            title: t('superadmin:columns.endDate'),
            dataIndex: 'subscriptionEndDate',
            key: 'endDate',
            render: (date) => date ? new Date(date).toLocaleDateString() : t('superadmin:table.na')
        },
        {
            title: t('superadmin:columns.yearlyAmount'),
            dataIndex: 'yearlyAmount',
            key: 'yearlyAmount',
            render: (amount) => amount > 0 ? `$${amount.toFixed(2)}` : t('superadmin:table.free')
        },
        {
            title: t('superadmin:columns.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title={t('superadmin:actions.extendSubscription')}>
                        <Button
                            size="small"
                            icon={<CalendarOutlined />}
                            onClick={() => {
                                setSelectedClinic(record);
                                extendForm.setFieldsValue({ days: 365, amount: record.yearlyAmount });
                                setExtendModalVisible(true);
                            }}
                        />
                    </Tooltip>

                    <Tooltip title={t('superadmin:actions.updateDentistLimit')}>
                        <Button
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={() => {
                                setSelectedClinic(record);
                                limitForm.setFieldsValue({ dentistLimit: record.dentistLimit });
                                setLimitModalVisible(true);
                            }}
                        />
                    </Tooltip>

                    {record.subscriptionStatus === 'suspended' ? (
                        <Tooltip title={t('superadmin:actions.activate')}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => handleStatusChange(record.id, 'active')}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title={t('superadmin:actions.suspend')}>
                            <Button
                                size="small"
                                danger
                                icon={<StopOutlined />}
                                onClick={() => handleStatusChange(record.id, 'suspended')}
                            />
                        </Tooltip>
                    )}

                    <Popconfirm
                        title={t('superadmin:actions.deleteClinic')}
                        description={t('superadmin:actions.deleteConfirm')}
                        onConfirm={() => handleDeleteClinic(record.id)}
                        okText={t('superadmin:actions.yes')}
                        cancelText={t('superadmin:actions.no')}
                        okButtonProps={{ danger: true }}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>{t('superadmin:title')}</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                    style={{ background: '#769382' }}
                >
                    {t('superadmin:createClinic')}
                </Button>
            </div>

            {/* Statistics */}
            {statistics && (
                <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title={t('superadmin:stats.totalClinics')}
                                    value={statistics.clinics.total}
                                    prefix={<BankOutlined />}
                                    valueStyle={{ color: '#769382' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title={t('superadmin:stats.activeClinics')}
                                    value={statistics.clinics.active}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title={t('superadmin:stats.totalDentists')}
                                    value={statistics.users.dentists}
                                    prefix={<TeamOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <Statistic
                                    title={t('superadmin:stats.trialClinics')}
                                    value={statistics.clinics.trial}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Expiring Soon Alert */}
                    {statistics.expiringSoon && statistics.expiringSoon.length > 0 && (
                        <Alert
                            message={t('superadmin:expiringSoon', { count: statistics.expiringSoon.length })}
                            description={
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {statistics.expiringSoon.map(clinic => (
                                        <li key={clinic.id}>
                                            <strong>{clinic.name}</strong> - {t('superadmin:daysRemaining', { days: clinic.remainingDays })}
                                            ({new Date(clinic.subscriptionEndDate).toLocaleDateString()})
                                        </li>
                                    ))}
                                </ul>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />
                    )}
                </>
            )}

            {/* Search and Filter */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                        <Input.Search
                            placeholder={t('superadmin:table.searchPlaceholder')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={() => fetchData(1, pagination.pageSize)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder={t('superadmin:table.filterByStatus')}
                            value={statusFilter || undefined}
                            onChange={(value) => {
                                setStatusFilter(value || '');
                                fetchData(1, pagination.pageSize);
                            }}
                            allowClear
                            style={{ width: '100%' }}
                        >
                            <Option value="active">{t('superadmin:status.active')}</Option>
                            <Option value="trial">{t('superadmin:status.trial')}</Option>
                            <Option value="expired">{t('superadmin:status.expired')}</Option>
                            <Option value="suspended">{t('superadmin:status.suspended')}</Option>
                            <Option value="inactive">{t('superadmin:status.inactive')}</Option>
                        </Select>
                    </Col>
                    <Col>
                        <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
                            {t('superadmin:refresh')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Clinics Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={clinics}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        ...pagination,
                        onChange: (page, pageSize) => fetchData(page, pageSize),
                        showSizeChanger: true,
                        showTotal: (total) => t('superadmin:table.showingTotal', { total })
                    }}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Create Clinic Modal */}
            <Modal
                title={t('superadmin:createModal.title')}
                open={createModalVisible}
                onCancel={() => { setCreateModalVisible(false); form.resetFields(); }}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateClinic}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="name" label={t('superadmin:createModal.clinicName')} rules={[{ required: true }]}>
                                <Input placeholder={t('superadmin:createModal.clinicNamePlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="email" label={t('superadmin:createModal.clinicEmail')} rules={[{ required: true, type: 'email' }]}>
                                <Input placeholder={t('superadmin:createModal.clinicEmailPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label={t('superadmin:createModal.phone')} rules={[{ required: true }]}>
                                <Input placeholder={t('superadmin:createModal.phonePlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="address" label={t('superadmin:createModal.address')}>
                                <Input placeholder={t('superadmin:createModal.addressPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Card title={t('superadmin:createModal.adminAccount')} size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="adminName" label={t('superadmin:createModal.adminName')} rules={[{ required: true }]}>
                                    <Input placeholder={t('superadmin:createModal.adminNamePlaceholder')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="adminEmail" label={t('superadmin:createModal.adminEmail')} rules={[{ required: true, type: 'email' }]}>
                                    <Input placeholder={t('superadmin:createModal.adminEmailPlaceholder')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="adminPassword" label={t('superadmin:createModal.adminPassword')} rules={[{ required: true, min: 6 }]}>
                                    <Input.Password placeholder={t('superadmin:createModal.adminPasswordPlaceholder')} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card title={t('superadmin:createModal.allowedRoles', 'Allowed User Roles')} size="small" style={{ marginBottom: 16 }}>
                        <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                            {t('superadmin:createModal.allowedRolesDesc', 'Select which user roles this admin can create')}
                        </p>
                        <Form.Item name="allowedRoles" initialValue={['dentist', 'receptionist']} noStyle>
                            <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Checkbox value="dentist">{t('superadmin:roles.dentist', 'Dentist')}</Checkbox>
                                <Checkbox value="receptionist">{t('superadmin:roles.receptionist', 'Receptionist/Secretary')}</Checkbox>
                                <Checkbox value="pharmacy">{t('superadmin:roles.pharmacy', 'Pharmacy Technician')}</Checkbox>
                                <Checkbox value="lab_technician">{t('superadmin:roles.lab_technician', 'Lab Technician')}</Checkbox>
                                <Checkbox value="radiologist">{t('superadmin:roles.radiologist', 'Radiologist')}</Checkbox>
                            </Checkbox.Group>
                        </Form.Item>
                    </Card>

                    <Card title={t('superadmin:createModal.subscriptionSettings')} size="small">
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="dentistLimit" label={t('superadmin:createModal.dentistLimit')} initialValue={5}>
                                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="yearlyAmount" label={t('superadmin:createModal.yearlyAmount')} initialValue={0}>
                                    <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="subscriptionPeriod" label={t('superadmin:createModal.periodDays')} initialValue={365}>
                                    <InputNumber min={1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>
                                {t('superadmin:createModal.cancel')}
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading} style={{ background: '#769382' }}>
                                {t('superadmin:createModal.create')}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Extend Subscription Modal */}
            <Modal
                title={t('superadmin:extendModal.title', { clinicName: selectedClinic?.name })}
                open={extendModalVisible}
                onCancel={() => { setExtendModalVisible(false); extendForm.resetFields(); }}
                footer={null}
            >
                <Form form={extendForm} layout="vertical" onFinish={handleExtendSubscription}>
                    <Form.Item name="days" label={t('superadmin:extendModal.extensionPeriod')} rules={[{ required: true }]}>
                        <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="amount" label={t('superadmin:extendModal.paymentAmount')}>
                        <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="$" />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setExtendModalVisible(false); extendForm.resetFields(); }}>
                                {t('superadmin:extendModal.cancel')}
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading} style={{ background: '#769382' }}>
                                {t('superadmin:extendModal.extend')}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* Update Dentist Limit Modal */}
            <Modal
                title={t('superadmin:limitModal.title', { clinicName: selectedClinic?.name })}
                open={limitModalVisible}
                onCancel={() => { setLimitModalVisible(false); limitForm.resetFields(); }}
                footer={null}
            >
                <Form form={limitForm} layout="vertical" onFinish={handleUpdateDentistLimit}>
                    <Form.Item name="dentistLimit" label={t('superadmin:limitModal.dentistLimit')} rules={[{ required: true }]}>
                        <InputNumber min={1} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => { setLimitModalVisible(false); limitForm.resetFields(); }}>
                                {t('superadmin:limitModal.cancel')}
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading} style={{ background: '#769382' }}>
                                {t('superadmin:limitModal.update')}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default SuperAdminDashboard;
